import React, { useEffect, useState } from "react";
import { Loader2, LogOut, Search, Flame } from "lucide-react";
import { toast } from "sonner";
import { analyticsService, formatApiError } from "../../services/catalogService";

// Task 22 — search analytics + Task 23 — navigation heat zones.
export default function AnalyticsPanel({ admin, onLogout }) {
  const [searches, setSearches] = useState(null);
  const [interactions, setInteractions] = useState(null);

  useEffect(() => {
    analyticsService.topSearches().then(setSearches).catch((e) => toast.error("Could not load searches", { description: formatApiError(e) }));
    analyticsService.interactions().then(setInteractions).catch((e) => toast.error("Could not load heatmap", { description: formatApiError(e) }));
  }, []);

  const maxClicks = Math.max(1, ...(interactions?.hotspots || []).map((h) => h.clicks));
  const maxTraffic = Math.max(1, ...(interactions?.pages || []).map((p) => p.views + p.clicks));

  return (
    <div className="max-w-4xl mx-auto" data-testid="admin-analytics-panel">
      <div className="flex items-center justify-between mb-10 gap-4 flex-wrap">
        <div>
          <span className="font-sans text-xs uppercase tracking-[0.24em] text-[#D92A47]">Admin</span>
          <h1 className="font-serif text-4xl md:text-5xl text-[#252525] mt-2">Analytics</h1>
          <p className="text-[#817B74] text-sm mt-1">
            What visitors search and touch, most frequent first. Signed in as {admin.email}
          </p>
        </div>
        <button onClick={onLogout} data-testid="admin-logout"
          className="inline-flex items-center gap-2 h-10 px-4 border border-black/15 text-[11px] uppercase tracking-[0.14em] hover:border-[#D92A47] hover:text-[#D92A47]">
          <LogOut size={14} /> Sign out
        </button>
      </div>

      {/* Task 23 — hot zones */}
      <div className="bg-white border border-black/10 p-6 md:p-8 mb-8" data-testid="analytics-heatmap">
        <span className="text-[11px] uppercase tracking-[0.14em] text-[#817B74] flex items-center gap-2">
          <Flame size={13} className="text-[#D92A47]" /> Hot zones — most touched pages
        </span>
        {interactions === null ? (
          <div className="text-[#817B74] py-12 text-center"><Loader2 className="animate-spin inline mr-2" size={16} />Loading…</div>
        ) : interactions.pages.length === 0 ? (
          <p className="py-10 text-center text-[#817B74]" data-testid="heatmap-empty">
            No navigation data yet — it appears as visitors browse the site.
          </p>
        ) : (
          <>
            <div className="space-y-2 mt-4" data-testid="heatmap-pages">
              {interactions.pages.slice(0, 8).map((p) => (
                <div key={p.path} className="flex items-center gap-3" data-testid={`heatmap-page-${p.path.replace(/\W+/g, "-")}`}>
                  <span className="w-32 shrink-0 truncate text-sm font-medium text-[#252525]" title={p.path}>{p.path}</span>
                  <div className="flex-1 h-6 bg-[#F6F2EC] relative overflow-hidden">
                    <div
                      className="absolute inset-y-0 left-0"
                      style={{
                        width: `${Math.max(4, ((p.views + p.clicks) / maxTraffic) * 100)}%`,
                        backgroundColor: `rgba(217, 42, 71, ${0.25 + 0.75 * ((p.views + p.clicks) / maxTraffic)})`,
                      }}
                    />
                  </div>
                  <span className="w-36 shrink-0 text-xs text-[#817B74] text-right">
                    {p.views} views · {p.clicks} clicks
                  </span>
                </div>
              ))}
            </div>

            {interactions.hotspots.length > 0 && (
              <div className="mt-8">
                <span className="text-[11px] uppercase tracking-[0.14em] text-[#817B74]">Most clicked elements</span>
                <div className="space-y-2 mt-3" data-testid="heatmap-hotspots">
                  {interactions.hotspots.slice(0, 10).map((h) => (
                    <div key={`${h.path}-${h.target}`} className="flex items-center gap-3" data-testid={`hotspot-${h.target}`}>
                      <span className="w-56 shrink-0 truncate text-xs text-[#252525]" title={`${h.target} (${h.path})`}>
                        {h.target}
                        <span className="text-[#817B74]"> · {h.path}</span>
                      </span>
                      <div className="flex-1 h-4 bg-[#F6F2EC] relative overflow-hidden">
                        <div
                          className="absolute inset-y-0 left-0"
                          style={{
                            width: `${Math.max(4, (h.clicks / maxClicks) * 100)}%`,
                            backgroundColor: `rgba(196, 164, 107, ${0.3 + 0.7 * (h.clicks / maxClicks)})`,
                          }}
                        />
                      </div>
                      <span className="w-12 shrink-0 text-xs text-[#817B74] text-right">{h.clicks}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Task 22 — top searches */}
      <div className="bg-white border border-black/10 p-6 md:p-8">
        <span className="text-[11px] uppercase tracking-[0.14em] text-[#817B74]">Top catalog searches</span>
        {searches === null ? (
          <div className="text-[#817B74] py-16 text-center"><Loader2 className="animate-spin inline mr-2" size={16} />Loading…</div>
        ) : searches.length === 0 ? (
          <div className="py-12 text-center text-[#817B74]" data-testid="analytics-empty">
            <Search size={22} strokeWidth={1.4} className="mx-auto mb-3 text-[#D92A47]" />
            No searches recorded yet — they appear here as visitors use the catalog search.
          </div>
        ) : (
          <table className="w-full text-sm mt-4" data-testid="analytics-searches-table">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-[0.14em] text-[#817B74] border-b border-black/10">
                <th className="py-2 pr-4">Term</th>
                <th className="py-2 pr-4">Searches</th>
                <th className="py-2 pr-4">Avg. results</th>
                <th className="py-2">Last searched</th>
              </tr>
            </thead>
            <tbody>
              {searches.map((s) => (
                <tr key={s.term} className="border-b border-black/5" data-testid={`search-term-${s.term.replace(/\s+/g, "-")}`}>
                  <td className="py-2.5 pr-4 font-medium text-[#252525]">{s.term}</td>
                  <td className="py-2.5 pr-4">{s.count}</td>
                  <td className="py-2.5 pr-4">{s.avg_results}</td>
                  <td className="py-2.5 text-[#817B74]">
                    {s.last_at ? new Date(s.last_at).toLocaleDateString("en-GB", { day: "2-digit", month: "short" }) : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
